import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import type { CreateAnalysisRequestDto } from '@inference.engine/api-client/web';
import { findAnalysisModelEntry } from './modelOptions';

export type SelectionMode = 'auto' | 'manual';
export type CompetitorTab = 'document' | 'manual';

export interface ManualEntry {
  id: string;
  name: string;
  details: string;
}

interface BrandGoalData {
  reportName: string;
  brandName: string;
  /** The redesigned step collects a list; `brandWebsite` is the single-field one it replaces. */
  brandWebsites: string[];
  brandWebsite: string;
  analysisGoal: string;
  targetMarkets: string[];
}

/** Which of the two ways of naming the audience yourself the step is showing. */
export type AudienceSource = 'hub' | 'upload';

/**
 * One audience read out of an uploaded document, as the step holds it.
 *
 * The id is minted here: the extraction answers with a list, not with addressable rows, and
 * the step needs a stable key to select on - two documents can describe audiences with the
 * same name.
 */
export interface ExtractedAudience {
  id: string;
  name: string;
  description?: string;
  /** The age the documents gave them, as a "min-max" range. Absent when they gave none. */
  ageRange?: string;
  tags?: string[];
  /**
   * Everything the documents said about this audience. Not shown - the card carries the one
   * line - but sent with it, because this is what the report researches and what the persona
   * is built from.
   */
  context?: string;
  sourceFileName?: string;
  /**
   * Where in that document the audience sits, in the document's own words. Shown on the card
   * so a reader can go and check it, and not sent with the report: it says where the text was
   * found, not anything about the audience.
   */
  sourceLocation?: string;
}

export type ExtractionStatus = 'idle' | 'uploading' | 'reading' | 'ready' | 'failed';

/**
 * The document read in progress.
 *
 * Kept here rather than in the tab that starts it, because the tab unmounts whenever the
 * wizard moves on or the audience source is switched - and a run that lived only in its state
 * died with it, while the reading carried on server-side with nobody left to collect it.
 */
export interface ExtractionRun {
  /** null until the documents are uploaded and the run exists server-side. */
  id: string | null;
  status: ExtractionStatus;
  /** The documents being read, for the UI to name them. */
  fileNames: string[];
  /**
   * Kept so a failure before the run existed can be tried again: the upload is what fails most
   * often, and by then there is no run to ask a second time.
   */
  files: File[];
  /** The run whose segments already reached the list, so resuming does not add them twice. */
  deliveredRunId: string | null;
  /**
   * When this client first saw the run, read only while the server has not dated it.
   *
   * The server dates a run when the claim lands, and the claim is a separate round trip from
   * the insert that created it. A run whose claim never landed therefore has nothing to expire
   * against, and the poll would ask every two seconds for as long as the tab stayed open while
   * the retry never appeared.
   */
  waitingSince?: number;
  error?: string;
}

export const initialExtractionRun: ExtractionRun = {
  id: null,
  status: 'idle',
  fileNames: [],
  files: [],
  deliveredRunId: null,
};

/** Whether the reading is still happening, which is what makes it worth asking before losing. */
export const isExtractionInFlight = (run: ExtractionRun): boolean =>
  run.status === 'uploading' || run.status === 'reading';

export interface AnalysisFormState {
  brandGoal: BrandGoalData;
  /** null until the user picks, so the step cannot be advanced by not deciding. */
  audienceMode: SelectionMode | null;
  /** Also null until picked. The payload reads null as auto, which is what IQ does anyway. */
  competitorsMode: SelectionMode | null;
  /** null means never picked, so the payload omits it and the server default applies. */
  model: string | null;
  // Audience state
  selectedPersonaIds: string[];
  audienceSource: AudienceSource;
  /** Every audience read so far, across all the documents uploaded in this step. */
  extractedAudiences: ExtractedAudience[];
  selectedExtractedIds: string[];
  /** The ones asked to be kept in the Knowledge Hub once their persona is built. */
  extractedToSaveIds: string[];
  extraction: ExtractionRun;
  // Competitor state
  competitorTab: CompetitorTab;
  competitorUploadedFiles: File[];
  manualCompetitors: ManualEntry[];
  // Setters
  setBrandGoal: (data: Partial<BrandGoalData>) => void;
  addBrandWebsite: (url: string) => void;
  removeBrandWebsite: (url: string) => void;
  setAudienceMode: (mode: SelectionMode) => void;
  setCompetitorsMode: (mode: SelectionMode) => void;
  setModel: (model: string) => void;
  addSelectedPersonaId: (id: string) => void;
  removeSelectedPersonaId: (id: string) => void;
  setAudienceSource: (source: AudienceSource) => void;
  patchExtractionRun: (patch: Partial<ExtractionRun>) => void;
  resetExtractionRun: () => void;
  addExtractedAudiences: (audiences: ExtractedAudience[]) => void;
  clearExtractedAudiences: () => void;
  toggleExtractedAudience: (id: string) => void;
  toggleExtractedSaveToHub: (id: string) => void;
  setCompetitorTab: (tab: CompetitorTab) => void;
  addCompetitorUploadedFiles: (files: File[]) => void;
  removeCompetitorUploadedFile: (file: File) => void;
  addManualCompetitor: () => void;
  updateManualCompetitor: (id: string, data: Partial<Omit<ManualEntry, 'id'>>) => void;
  removeManualCompetitor: (id: string) => void;
  reset: () => void;
}

const initialBrandGoal: BrandGoalData = {
  reportName: '',
  brandName: '',
  brandWebsites: [],
  brandWebsite: '',
  analysisGoal: '',
  targetMarkets: [],
};

const createEmptyEntry = (): ManualEntry => ({
  id: uuidv4(),
  name: '',
  details: '',
});

/** Mirrors WEBSITES_MAX in the API's analysis.dto.ts, which rejects a longer list. */
export const BRAND_WEBSITES_MAX = 10;

/** Mirrors UPLOADED_AUDIENCES_MAX in the API's analysis.dto.ts, for the same reason. */
export const UPLOADED_AUDIENCES_MAX = 10;

export const useAnalysisFormStore = create<AnalysisFormState>((set) => ({
  brandGoal: initialBrandGoal,
  audienceMode: null,
  competitorsMode: null,
  model: null,
  // Audience
  selectedPersonaIds: [],
  audienceSource: 'hub',
  extractedAudiences: [],
  selectedExtractedIds: [],
  extractedToSaveIds: [],
  extraction: initialExtractionRun,
  // Competitors
  competitorTab: 'document',
  competitorUploadedFiles: [],
  manualCompetitors: [createEmptyEntry()],
  // Setters
  setBrandGoal: (data) => set((state) => ({ brandGoal: { ...state.brandGoal, ...data } })),
  addBrandWebsite: (url) =>
    set((state) => ({
      brandGoal: {
        ...state.brandGoal,
        brandWebsites: state.brandGoal.brandWebsites.includes(url)
          ? state.brandGoal.brandWebsites
          : [...state.brandGoal.brandWebsites, url],
      },
    })),
  removeBrandWebsite: (url) =>
    set((state) => ({
      brandGoal: {
        ...state.brandGoal,
        brandWebsites: state.brandGoal.brandWebsites.filter((w) => w !== url),
      },
    })),
  // Choosing 'auto' drops any personas already picked, so the store cannot hold a selection
  // that contradicts the mode. `buildCreateAnalysisPayload` guards against sending them anyway,
  // but the next reader - analytics, a summary step - would otherwise report an audience the
  // user opted out of.
  setAudienceMode: (audienceMode) =>
    set(
      audienceMode === 'auto'
        ? {
            audienceMode,
            selectedPersonaIds: [],
            extractedAudiences: [],
            selectedExtractedIds: [],
            extractedToSaveIds: [],
            extraction: initialExtractionRun,
          }
        : { audienceMode },
    ),
  setCompetitorsMode: (competitorsMode) => set({ competitorsMode }),
  setModel: (model) => set({ model }),
  addSelectedPersonaId: (id) =>
    set((state) => ({
      selectedPersonaIds: state.selectedPersonaIds.includes(id)
        ? state.selectedPersonaIds
        : [...state.selectedPersonaIds, id],
    })),
  removeSelectedPersonaId: (id) =>
    set((state) => ({
      selectedPersonaIds: state.selectedPersonaIds.filter((pid) => pid !== id),
    })),
  // One source per report, so moving to the other tab drops what the first one had. The step
  // asks before calling this when there is something to lose; the store just keeps the two
  // from ever being submitted together.
  setAudienceSource: (audienceSource) =>
    set(
      audienceSource === 'hub'
        ? {
            audienceSource,
            extractedAudiences: [],
            selectedExtractedIds: [],
            extractedToSaveIds: [],
            extraction: initialExtractionRun,
          }
        : { audienceSource, selectedPersonaIds: [] },
    ),
  // Documents add up: a report is built from everything the person handed over, and the
  // reading of each one is a separate round trip. Selections already made are left alone -
  // a second document must not cost the picks from the first.
  //
  // A segment already read from the same document is dropped rather than listed twice, which
  // is what the same file uploaded twice would otherwise produce.
  addExtractedAudiences: (audiences) =>
    set((state) => {
      const seen = new Set(
        state.extractedAudiences.map((a) => `${a.sourceFileName ?? ''}::${a.name}`),
      );
      const fresh = audiences.filter((a) => !seen.has(`${a.sourceFileName ?? ''}::${a.name}`));

      return { extractedAudiences: [...state.extractedAudiences, ...fresh] };
    }),
  patchExtractionRun: (patch) =>
    set((state) => ({ extraction: { ...state.extraction, ...patch } })),
  resetExtractionRun: () => set({ extraction: initialExtractionRun }),
  clearExtractedAudiences: () =>
    set({ extractedAudiences: [], selectedExtractedIds: [], extractedToSaveIds: [] }),
  toggleExtractedAudience: (id) =>
    set((state) => ({
      selectedExtractedIds: state.selectedExtractedIds.includes(id)
        ? state.selectedExtractedIds.filter((sid) => sid !== id)
        : // Refused rather than sent and rejected: the API caps the list, and finding that out
          // at the last click of the wizard costs the whole submission.
          state.selectedExtractedIds.length >= UPLOADED_AUDIENCES_MAX
          ? state.selectedExtractedIds
          : [...state.selectedExtractedIds, id],
      // Unpicking an audience takes the keep with it: keeping one the report never used would
      // put a persona in the hub that nothing asked for.
      extractedToSaveIds: state.selectedExtractedIds.includes(id)
        ? state.extractedToSaveIds.filter((sid) => sid !== id)
        : state.extractedToSaveIds,
    })),
  // Only an audience the report is being built on can be kept, which is the same rule
  // `toggleExtractedAudience` keeps from the other side by dropping the keep when one is
  // unpicked. The payload carries the picked ones alone, so a keep on any other is an
  // instruction nothing acts on.
  toggleExtractedSaveToHub: (id) =>
    set((state) => ({
      extractedToSaveIds: state.extractedToSaveIds.includes(id)
        ? state.extractedToSaveIds.filter((sid) => sid !== id)
        : state.selectedExtractedIds.includes(id)
          ? [...state.extractedToSaveIds, id]
          : state.extractedToSaveIds,
    })),
  setCompetitorTab: (competitorTab) => set({ competitorTab }),
  addCompetitorUploadedFiles: (files) =>
    set((state) => ({ competitorUploadedFiles: [...state.competitorUploadedFiles, ...files] })),
  removeCompetitorUploadedFile: (file) =>
    set((state) => ({
      competitorUploadedFiles: state.competitorUploadedFiles.filter((f) => f !== file),
    })),
  addManualCompetitor: () =>
    set((state) => ({
      manualCompetitors: [...state.manualCompetitors, createEmptyEntry()],
    })),
  updateManualCompetitor: (id, data) =>
    set((state) => ({
      manualCompetitors: state.manualCompetitors.map((c) => (c.id === id ? { ...c, ...data } : c)),
    })),
  removeManualCompetitor: (id) =>
    set((state) => ({
      manualCompetitors: state.manualCompetitors.filter((c) => c.id !== id),
    })),
  reset: () =>
    set({
      brandGoal: initialBrandGoal,
      audienceMode: null,
      audienceSource: 'hub',
      extractedAudiences: [],
      selectedExtractedIds: [],
      extractedToSaveIds: [],
      extraction: initialExtractionRun,
      competitorsMode: null,
      model: null,
      selectedPersonaIds: [],
      competitorTab: 'document',
      competitorUploadedFiles: [],
      manualCompetitors: [createEmptyEntry()],
    }),
}));

export const buildCreateAnalysisPayload = (): CreateAnalysisRequestDto => {
  const state = useAnalysisFormStore.getState();
  const {
    brandGoal,
    selectedPersonaIds,
    audienceMode,
    audienceSource,
    extractedAudiences,
    selectedExtractedIds,
    extractedToSaveIds,
    competitorsMode,
    manualCompetitors,
    model,
  } = state;

  const payload: CreateAnalysisRequestDto = {
    query: brandGoal.analysisGoal,
    brandName: brandGoal.brandName,
  };

  const reportName = brandGoal.reportName.trim();
  if (reportName) {
    payload.name = reportName;
  }

  if (brandGoal.brandWebsites.length > 0) {
    payload.websites = brandGoal.brandWebsites;
  } else if (brandGoal.brandWebsite) {
    payload.website = brandGoal.brandWebsite;
  }

  const markets = brandGoal.targetMarkets.map((m) => m.trim()).filter(Boolean);
  if (markets.length > 0) {
    payload.markets = markets;
  }

  // Letting IQ suggest the audience is the absence of personas: the prompt only pins the
  // audience down when it receives some. Only an explicit 'auto' drops them, because the
  // previous step never sets a mode and requires personas to get past, so reading no mode
  // as auto would silently discard what it made the user pick.
  // One source per report, and the tab is what decides which: the store already clears the
  // other side, so this only has to send what the active one holds.
  if (audienceMode !== 'auto' && audienceSource === 'hub' && selectedPersonaIds.length > 0) {
    payload.personaIds = selectedPersonaIds;
  }

  if (audienceMode !== 'auto' && audienceSource === 'upload') {
    const chosen = extractedAudiences.filter((audience) =>
      selectedExtractedIds.includes(audience.id),
    );
    if (chosen.length > 0) {
      payload.uploadedAudiences = chosen.map(
        ({ name, description, ageRange, tags, context, sourceFileName, id }) => ({
          name,
          ...(description ? { description } : {}),
          ...(ageRange ? { ageRange } : {}),
          ...(tags?.length ? { tags } : {}),
          ...(context ? { context } : {}),
          ...(sourceFileName ? { sourceFileName } : {}),
          saveToHub: extractedToSaveIds.includes(id),
        }),
      );
    }
  }

  const modelEntry = model === null ? undefined : findAnalysisModelEntry(model);
  if (modelEntry) {
    payload.model = modelEntry.model;
    payload.provider = modelEntry.provider;
  }

  payload.competitorsMode = competitorsMode === 'manual' ? 'custom' : 'auto';
  if (competitorsMode === 'manual') {
    const filledCompetitors = manualCompetitors
      .filter((c) => c.name.trim() !== '')
      .map((c) => c.name);
    if (filledCompetitors.length > 0) {
      payload.competitorsList = filledCompetitors;
    }
  }

  return payload;
};
